import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { MessageCircle, X, Send, Sparkles, Loader2 } from 'lucide-react';
import api from '../lib/api';

const QUICK_PROMPTS = [
  '¿Qué hago si tuve una brecha de seguridad?',
  '¿Cómo genero el RAT?',
  '¿Cuándo necesito una DPIA?',
  '¿Qué es una solicitud ARCO+?',
];

export default function Copilot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: '¡Hola! Soy el Copiloto Priviax. Puedo responder dudas sobre la Ley 21.719 y guiarte paso a paso dentro del sistema. ¿En qué te ayudo?' },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Auto-crecer el textarea verticalmente (ancho fijo, alto crece hasta 140px)
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, [input, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post('/copilot/chat', {
        message: msg, session_id: sessionId, history: messages.slice(-8),
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const quotaHit = d && typeof d === 'object' && d.error === 'quota_exceeded';
      const errMsg = quotaHit
        ? `${d.message}\n\nSolicite un aumento de plan desde Configuración → Plan.`
        : `Disculpe, no pude responder. ${typeof d === 'string' ? d : 'Intente de nuevo en un momento.'}`;
      setMessages((m) => [...m, { role: 'assistant', content: errMsg }]);
    } finally { setSending(false); }
  };

  return (
    <>
      {/* Botón burbuja */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#143A34] hover:bg-[#0F2A26] text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-105"
          data-testid="copilot-open"
          title="Copiloto Priviax"
        >
          <div className="relative">
            <MessageCircle size={22} strokeWidth={2} />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#10B981] border-2 border-[#143A34]" />
          </div>
        </button>
      )}

      {/* Ventana */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-50 w-[380px] max-w-[calc(100vw-2.5rem)] h-[560px] max-h-[calc(100vh-2.5rem)] bg-white shadow-2xl border border-[#D1D5DB] flex flex-col"
          data-testid="copilot-window"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-[#143A34] text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={14} />
              <div>
                <div className="text-[13px] font-semibold leading-tight">Copiloto Priviax</div>
                <div className="text-[10px] text-white/75">Ley 21.719 · Guía del sistema</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded" data-testid="copilot-close">
              <X size={14} />
            </button>
          </div>

          {/* Mensajes */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-[#FAFBFC]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-[#143A34] text-white'
                    : 'bg-white border border-[#E5E7EB] text-[#1C2329]'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#E5E7EB] px-3 py-2 text-[12.5px] text-[#6B7280] inline-flex items-center gap-1.5">
                  <Loader2 size={12} className="animate-spin" /> Pensando…
                </div>
              </div>
            )}
          </div>

          {/* Quick prompts (solo inicial) */}
          {messages.length <= 1 && !sending && (
            <div className="px-3 py-2 border-t border-[#E5E7EB] flex flex-wrap gap-1.5">
              {QUICK_PROMPTS.map((q) => (
                <button key={q} onClick={() => send(q)}
                  className="text-[10.5px] px-2 py-1 border border-[#D1D5DB] text-[#374151] hover:border-[#143A34] hover:text-[#143A34] hover:bg-[#F0F7F5]">
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-[#E5E7EB] p-2 flex items-end gap-2 bg-white">
            <textarea
              ref={inputRef}
              rows={1}
              className="flex-1 px-3 py-2 border border-[#D1D5DB] text-[12.5px] leading-snug focus:outline-none focus:border-[#143A34] resize-none overflow-y-auto"
              placeholder="Escriba su pregunta…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              disabled={sending}
              data-testid="copilot-input"
              style={{ minHeight: 36, maxHeight: 140 }}
            />
            <button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="px-3 py-2 bg-[#143A34] hover:bg-[#0F2A26] text-white text-[12px] disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              data-testid="copilot-send"
              style={{ height: 36 }}
            >
              <Send size={13} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
